import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { 
  CheckCircle2, AlertTriangle, FileText, Users, ChevronRight,
  Shield, Server, DollarSign, Settings
} from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";

const SEVERITY_COLORS = {
  Critical: "bg-red-600 text-white",
  High: "bg-red-500 text-white",
  Medium: "bg-amber-500 text-white",
  Low: "bg-blue-500 text-white",
  Observation: "bg-slate-500 text-white"
};

const CATEGORY_ICONS = {
  compliance: Shield,
  financial: DollarSign,
  it: Server,
  operational: Settings
};

const STATUS_BADGES = {
  draft: { label: "Draft", variant: "outline" },
  validated: { label: "Validated", variant: "default" },
  needs_more_evidence: { label: "Needs Evidence", variant: "destructive" }
};

export function ConsolidatedFindingsBoard({ findings = [], onViewDetails }) {
  if (findings.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-8 text-center">
          <CheckCircle2 className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">
            No consolidated findings yet. Team analysis will generate findings here.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Group by category
  const byCategory = findings.reduce((acc, f) => {
    const cat = f.category || "operational";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(f);
    return acc;
  }, {});

  // Summary stats
  const stats = {
    total: findings.length,
    validated: findings.filter(f => f.status === "validated").length,
    disputed: findings.filter(f => f.disputed).length,
    bySeverity: findings.reduce((acc, f) => {
      acc[f.severity] = (acc[f.severity] || 0) + 1;
      return acc;
    }, {})
  };

  return (
    <div className="space-y-4" data-testid="consolidated-findings-board">
      {/* Summary Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium">Consolidated Findings</h3>
          <p className="text-xs text-muted-foreground">
            Team-validated findings from multi-agent analysis
          </p>
        </div>
        <div className="flex gap-2">
          {Object.entries(stats.bySeverity).map(([sev, count]) => (
            <Badge key={sev} className={`text-xs ${SEVERITY_COLORS[sev] || SEVERITY_COLORS.Medium}`}>
              {count} {sev}
            </Badge>
          ))}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="bg-muted/20">
          <CardContent className="py-3 text-center">
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-xs text-muted-foreground">Total Findings</p>
          </CardContent>
        </Card>
        <Card className="bg-emerald-500/10">
          <CardContent className="py-3 text-center">
            <p className="text-2xl font-bold text-emerald-500">{stats.validated}</p>
            <p className="text-xs text-muted-foreground">Validated</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-500/10">
          <CardContent className="py-3 text-center">
            <p className="text-2xl font-bold text-amber-500">{stats.disputed}</p>
            <p className="text-xs text-muted-foreground">Disputed</p>
          </CardContent>
        </Card>
      </div>

      {/* Findings by Category */}
      {Object.entries(byCategory).map(([category, catFindings]) => {
        const CategoryIcon = CATEGORY_ICONS[category] || Settings;
        
        return (
          <Card key={category}>
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <CategoryIcon className="w-4 h-4" />
                {category.charAt(0).toUpperCase() + category.slice(1)} Findings
                <Badge variant="outline" className="ml-auto text-xs">
                  {catFindings.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table className="table-dense">
                <TableHeader>
                  <TableRow>
                    <TableHead>Finding</TableHead>
                    <TableHead>Control</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Contributors</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {catFindings.map((finding) => {
                    const statusConfig = STATUS_BADGES[finding.status] || STATUS_BADGES.draft;
                    
                    return (
                      <TableRow key={finding.id}>
                        <TableCell>
                          <div className="max-w-[300px]">
                            <p className="font-medium text-sm truncate">{finding.finding_title}</p>
                            {finding.disputed && (
                              <Badge variant="outline" className="text-xs text-amber-500 border-amber-500 mt-1">
                                <AlertTriangle className="w-3 h-3 mr-1" /> Disputed
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-xs">{finding.control_id || "-"}</span>
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${SEVERITY_COLORS[finding.severity] || SEVERITY_COLORS.Medium}`}>
                            {finding.severity}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={statusConfig.variant} className="text-xs">
                            {statusConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Users className="w-3 h-3 text-muted-foreground" />
                            <span className="text-xs">{finding.contributing_agents?.length || 0}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 w-6 p-0"
                            onClick={() => onViewDetails?.(finding)}
                          >
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        );
      })}

      {/* Remediation Summary */}
      {findings.some(f => f.remediation_actions?.length > 0) && (
        <Card className="bg-blue-500/5 border-blue-500/30">
          <CardHeader className="py-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-500" />
              Remediation Actions Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="py-2">
            <ul className="space-y-2">
              {findings
                .flatMap(f => f.remediation_actions || [])
                .slice(0, 10)
                .map((action, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <ChevronRight className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                    {action}
                  </li>
                ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default ConsolidatedFindingsBoard;
